import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import SelectBox from './select-box'
import { performanceDates, granularityFilterData } from '../core/variables'
import { fixedDates } from '../helpers/report-module'
import useLogAction from '../hooks/useLogAction'
import styles from '../styles/lost-links-data-summary.module.scss'
import StyledDatePicker from './date-picker'

const yesterday = new Date(Date.now())
yesterday.setDate(yesterday.getDate() - 1)

interface LostLinksDataSummaryProps {
  dataConfig: DataConfig
  setDataConfig: React.Dispatch<React.SetStateAction<DataConfig>>
  refetchData: (data: DataConfig, options?: RefetchOptions) => Promise<void>
  setFiltersHaveChanged: React.Dispatch<React.SetStateAction<boolean>>
}

export default function LostLinksDataSummary({
  dataConfig,
  setDataConfig,
  refetchData,
  setFiltersHaveChanged,
}: LostLinksDataSummaryProps) {
  const logAction = useLogAction()

  // const [calendarState, setCalendarState] = useState<CalendarState>({
  //   isVisible: false,
  // })

  const [showCalendar, setShowCalendar] = useState(
    dataConfig.startDate === 'custom',
  )
  const [customDates, setCustomDates] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])

  // Placeholder to show instead of 'custom date range'
  const customDateCopy = useMemo(() => {
    const [start, end] = customDates

    if (!start || !end) return 'custom date range'

    const st = moment(start).format('DD/MM/YYYY')
    const en = moment(end).format('DD/MM/YYYY')

    return `${st} - ${en}`
  }, [customDates])

  // Remove custom dates when not custom
  useEffect(() => {
    if (
      performanceDates.find((item) => item.metricValue === dataConfig.startDate)
    ) {
      setCustomDates([null, null])
    }
  }, [dataConfig.startDate])

  // Set custom dates for report to update
  useEffect(() => {
    const [start, end] = customDates

    if (!start || !end) return

    const st = moment(start).format('YYYY-MM-DD')
    const en = moment(end).format('YYYY-MM-DD')
    const days = moment(end).diff(start, 'days')

    const newDataConfig = {
      ...dataConfig,
      granularity: 'daily' as Granularity,
      startDate: st,
      endDate: en,
    }

    setDataConfig(newDataConfig)
    refetchData(newDataConfig)

    if (st !== en) {
      logAction({
        variables: {
          action: 'date-range-updated',
          extra: `custom - start: ${st} end: ${en} days: ${days}`,
          websiteSection: 'report',
          functionName: 'updateDateRange',
          pagePath: '/report/other-links-audit',
        },
      })
    }
  }, [customDates])

  return (
    <div className={styles.headerIntro}>
      <div className={styles.introCopyWrapper}>
        <div className={styles.introCopyBox}>
          <span className={styles.introCopy}>Show data for </span>
          <SelectBox
            variant="grey"
            className={styles.successMetricSelector}
            labelKey="displayName"
            valueKey="metricValue"
            isSearchable={false}
            defaultValue={performanceDates.find(
              (date) => date.metricValue === 'thisYear',
            )}
            placeholder={customDateCopy}
            value={
              showCalendar
                ? null
                : performanceDates.find((date) => {
                    if (
                      dataConfig.startDate &&
                      fixedDates.indexOf(dataConfig.startDate) > -1
                    ) {
                      return date.metricValue === dataConfig.startDate
                    }

                    return date.metricValue === 'custom'
                  })
            }
            options={performanceDates}
            onChange={async (newValue) => {
              if (!newValue) return

              const { metricValue } = newValue

              if (metricValue === 'custom') {
                // Do not update the date range until it has been selected
                setShowCalendar(true)
              } else {
                setShowCalendar(false)

                const newDataConfig = {
                  ...dataConfig,
                  startDate: metricValue,
                  endDate: undefined,
                }

                if (metricValue !== 'thisYear') {
                  newDataConfig.granularity = 'daily'
                }

                setDataConfig(newDataConfig)

                setFiltersHaveChanged(true)

                await refetchData(newDataConfig)

                logAction({
                  variables: {
                    action: 'date-range-updated-link-audit',
                    extra: newValue.metricValue,
                    websiteSection: 'report',
                    functionName: 'updateDateRange',
                    pagePath: '/report/other-links-audit',
                  },
                })
              }
            }}
          />
          {showCalendar && (
            <StyledDatePicker
              buttonOnly
              wrapperClassName={styles.dateWrapper}
              startOpen
              selectsRange
              startDate={customDates[0]}
              endDate={customDates[1]}
              onChange={(dates) => setCustomDates(dates)}
              maxDate={new Date(Date.now())}
            />
          )}
        </div>
        <div className={styles.introCopyBox}>
          <span className={styles.introCopy}> grouped by </span>
          <SelectBox
            variant="grey"
            className={styles.granularitySelector}
            labelKey="name"
            valueKey="value"
            isSearchable={false}
            defaultValue={granularityFilterData.find(
              (item) => item.value === 'monthly',
            )}
            value={granularityFilterData.find(
              (item) => item.value === dataConfig.granularity,
            )}
            options={granularityFilterData}
            onChange={async (newValue) => {
              if (!newValue) return

              const newDataConfig = {
                ...dataConfig,
                granularity: newValue.value as Granularity,
              }

              setDataConfig(newDataConfig)
              setFiltersHaveChanged(true)

              await refetchData(newDataConfig, {
                refetchTable: false,
              })

              logAction({
                variables: {
                  action: 'granularity-updated-link-audit',
                  extra: newValue.value,
                  websiteSection: 'report',
                  functionName: 'updateGranularity',
                  pagePath: '/report/other-links-audit',
                },
              })
            }}
          />
        </div>
      </div>
    </div>
  )
}
